import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react'
import { createPortal } from 'react-dom'

import { useTranslation } from '@/features/locale/LocaleProvider'
import { cn } from '@/lib/utils'

const SHEET_OPEN_CLASS = 'nutrimatic-mobile-sheet-open'
const SHEET_TRANSITION_MS = 480

export function useMobileVisualViewportLock(active: boolean) {
  useEffect(() => {
    if (!active || typeof window === 'undefined') return undefined
    const root = document.documentElement
    root.classList.add(SHEET_OPEN_CLASS)
    return () => root.classList.remove(SHEET_OPEN_CLASS)
  }, [active])
}

/** Sheet a pantalla completa compartido (browse + Nutrimatic vía className override). */
export function MobileFullScreenSheet({
  open,
  onClose,
  children,
  rootClassName = 'mobile-app__sheet-root',
  backdropClassName = 'mobile-app__sheet-backdrop',
  panelClassName = 'mobile-app__sheet-panel',
  inline = false,
}: {
  open: boolean
  onClose: () => void
  children: React.ReactNode
  rootClassName?: string
  backdropClassName?: string
  panelClassName?: string
  /** Si true, renderiza en el árbol DOM del padre (p. ej. panel trade desktop) sin portal. */
  inline?: boolean
}) {
  const t = useTranslation()
  const [mounted, setMounted] = useState(open)
  const [visible, setVisible] = useState(false)

  useMobileVisualViewportLock(inline ? false : mounted)

  useLayoutEffect(() => {
    if (!open) return
    setMounted(true)
    setVisible(false)
    let cancelled = false
    const frame = requestAnimationFrame(() => {
      if (cancelled) return
      requestAnimationFrame(() => {
        if (!cancelled) setVisible(true)
      })
    })
    return () => {
      cancelled = true
      cancelAnimationFrame(frame)
    }
  }, [open])

  useEffect(() => {
    if (open) return
    setVisible(false)
    const unmountTimer = window.setTimeout(() => setMounted(false), SHEET_TRANSITION_MS + 40)
    return () => window.clearTimeout(unmountTimer)
  }, [open])

  useEffect(() => {
    if (!mounted || !visible) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [mounted, onClose, visible])

  const handlePanelTransitionEnd = useCallback(
    (event: React.TransitionEvent<HTMLDivElement>) => {
      if (event.propertyName !== 'transform') return
      if (!open && !visible) setMounted(false)
    },
    [open, visible],
  )

  if (!mounted) return null

  const sheet = (
    <div
      className={cn(rootClassName, visible && `${rootClassName}--active`)}
      aria-hidden={!visible}
    >
      <button
        type="button"
        className={cn(backdropClassName, visible && `${backdropClassName}--visible`)}
        aria-label={t.common.close}
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        className={cn(panelClassName, visible && `${panelClassName}--visible`)}
        onTransitionEnd={handlePanelTransitionEnd}
      >
        {children}
      </div>
    </div>
  )

  if (inline) return sheet

  return createPortal(sheet, document.body)
}
